"use client";

import { Slider } from "@/components/ui/slider";
import { formatMoney, rulerBounds, type MarketBand } from "./price-check-model";

const STEP_CENTS = 5;

function share(cents: number, minCents: number, maxCents: number): number {
  const span = maxCents - minCents;
  return span > 0 ? Math.min(100, Math.max(0, ((cents - minCents) / span) * 100)) : 50;
}

function RulerBands({ band, at }: { band: MarketBand; at: (cents: number) => number }) {
  return (
    <div aria-hidden className="relative h-6">
      <span
        className="absolute bottom-0 h-3 rounded-t-sm bg-action-tint"
        style={{ left: `${at(band.minCents)}%`, width: `${at(band.maxCents) - at(band.minCents)}%` }}
      />
      <span
        className="absolute bottom-0 h-3 bg-action-fill/50"
        style={{ left: `${at(band.lowCents)}%`, width: `${at(band.highCents) - at(band.lowCents)}%` }}
      />
      <span
        className="absolute bottom-0 h-6 w-0.5 -translate-x-1/2 bg-foreground"
        style={{ left: `${at(band.medianCents)}%` }}
      />
    </div>
  );
}

function RulerLegend() {
  return (
    <ul className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-fg-muted">
      <li className="flex items-center gap-1.5">
        <span aria-hidden className="h-2 w-4 rounded-sm bg-action-tint" />
        Where the market prices
      </li>
      <li className="flex items-center gap-1.5">
        <span aria-hidden className="h-2 w-4 rounded-sm bg-action-fill/50" />
        Middle half of the market
      </li>
      <li className="flex items-center gap-1.5">
        <span aria-hidden className="h-3 w-0.5 bg-foreground" />
        Median
      </li>
    </ul>
  );
}

export function PriceRuler({
  band,
  priceCents,
  label,
  onChange,
}: {
  band: MarketBand;
  priceCents: number | null;
  label: string;
  onChange: (priceCents: number) => void;
}) {
  const { minCents, maxCents } = rulerBounds(band);
  const positionOf = (cents: number) => share(cents, minCents, maxCents);
  const value = Math.min(maxCents, Math.max(minCents, priceCents ?? band.medianCents));
  return (
    <div className="grid gap-2">
      <RulerBands band={band} at={positionOf} />
      <Slider
        min={minCents}
        max={maxCents}
        step={STEP_CENTS}
        value={[value]}
        onValueChange={([next]) => {
          if (next !== undefined) onChange(next);
        }}
        thumbLabels={[label]}
        thumbValueTexts={[formatMoney(value, band.currency)]}
      />
      <p className="flex justify-between tabular-nums text-xs text-fg-muted">
        <span>{formatMoney(minCents, band.currency)}</span>
        <span>{formatMoney(maxCents, band.currency)}</span>
      </p>
      <RulerLegend />
    </div>
  );
}
